import React from 'react'
import styled from 'styled-components'
import PlusIcon from '../../icons/IconLib/PlusIcon'
import XIcon from '../../icons/IconLib/XIcon'

export interface PhotoSlotTileProps {
  previewUrl: string | null
  label: string
  onPick: () => void
  onRemove: () => void
  /** Stretches to the cell instead of staying square — used by the tall camera cell. */
  fill?: boolean
}

/** Square slot: attached photo preview with a remove cross, or a neutral placeholder. */
const PhotoSlotTile: React.FC<PhotoSlotTileProps> = ({
  previewUrl,
  label,
  onPick,
  onRemove,
  fill = false,
}) => {
  if (!previewUrl) {
    return (
      <EmptySlot $fill={fill} type='button' onClick={onPick} aria-label={label} title={label}>
        <PlusIcon size={20} />
      </EmptySlot>
    )
  }

  return (
    <FilledSlot $fill={fill}>
      <Preview src={previewUrl} alt={label} />
      <RemoveButton
        type='button'
        onClick={onRemove}
        aria-label={`${label} — הסרה`}
        title='הסרה'
      >
        <XIcon size={13} />
      </RemoveButton>
    </FilledSlot>
  )
}

export default PhotoSlotTile

const slotShape = (fill: boolean) => `
  position: relative;
  width: 100%;
  ${fill ? 'height: 100%;' : 'aspect-ratio: 1;'}
  min-height: 0;
  border-radius: 0.875rem;
  overflow: hidden;
`

const EmptySlot = styled.button<{ $fill: boolean }>`
  ${({ $fill }) => slotShape($fill)}
  display: grid;
  place-items: center;
  padding: 0;
  border: 1.5px dashed ${({ theme }) => theme.colors.inputBorder};
  background: ${({ theme }) => theme.colors.bgLayout};
  color: ${({ theme }) => theme.colors.ink4};
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`

const FilledSlot = styled.div<{ $fill: boolean }>`
  ${({ $fill }) => slotShape($fill)}
  border: 1px solid ${({ theme }) => theme.colors.line};
  background: ${({ theme }) => theme.colors.line2};
`

const Preview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

/** Corner control, so it stays clear of the photo content in both directions. */
const RemoveButton = styled.button`
  position: absolute;
  top: 0.25rem;
  inset-inline-end: 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.375rem;
  height: 1.375rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.ink2};
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: background ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    background: ${({ theme }) => theme.colors.dangerSoft};
    color: ${({ theme }) => theme.colors.danger};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`
